import { useMemo } from 'react';
import { useFamily } from './useFamily';
import { usePersonProfiles } from './usePersonProfiles';

export interface UniversalContact {
  id: string;
  displayName: string;
  avatar_url?: string;
  source: 'family_member' | 'person_profile';
  // For family members
  profile_id?: string;
  child_name?: string;
  child_avatar_url?: string;
  role?: string;
  // For person profiles
  first_name?: string;
  last_name?: string;
  category?: string;
  family_role?: string;
  // Original data
  originalData: any;
}

export function useUniversalContacts(familyId?: string) {
  const { familyMembers, loading: familyLoading } = useFamily();
  const { persons, loading: personsLoading } = usePersonProfiles(familyId);

  const contacts = useMemo(() => {
    const universalContacts: UniversalContact[] = [];

    // Add family members (including the user and children stored in family_members)
    familyMembers.forEach(member => {
      // Only include members that have a profile_id (real users) or child_name (children)
      if (member.profile_id || member.child_name) {
        universalContacts.push({
          id: `fm_${member.id}`,
          displayName: member.profiles?.first_name && member.profiles?.last_name
            ? `${member.profiles.first_name} ${member.profiles.last_name}`
            : member.child_name || 'Membre sans nom',
          avatar_url: member.profiles?.avatar_url || member.child_avatar_url,
          source: 'family_member',
          profile_id: member.profile_id,
          child_name: member.child_name,
          child_avatar_url: member.child_avatar_url,
          role: member.role,
          originalData: member
        });
      }
    });

    // Add person profiles (contacts, extended family, etc.)
    persons.forEach(person => {
      // Skip person profiles that are already represented as family members with profile_id
      const alreadyInFamily = familyMembers.some(fm => 
        fm.profile_id && person.profile_id && fm.profile_id === person.profile_id
      );

      // Also skip children that are stored in both family_members and person_profiles
      const isDuplicateChild = familyMembers.some(fm => 
        fm.child_name && 
        person.first_name === fm.child_name.split(' ')[0] &&
        person.category === 'family'
      );

      if (!alreadyInFamily && !isDuplicateChild) {
        universalContacts.push({
          id: `pp_${person.id}`,
          displayName: `${person.first_name} ${person.last_name}`,
          avatar_url: person.avatar_url,
          source: 'person_profile',
          first_name: person.first_name,
          last_name: person.last_name,
          category: person.category,
          family_role: person.family_role,
          originalData: person
        });
      }
    });

    return universalContacts;
  }, [familyMembers, persons]);

  const loading = familyLoading || personsLoading;

  // Build id sets for fast lookup
  const profileIdSet = useMemo(() => new Set(familyMembers.map(fm => fm.profile_id).filter(Boolean) as string[]), [familyMembers]);
  const personProfileIdSet = useMemo(() => new Set(persons.map(p => p.id)), [persons]);

  // Group contacts by category
  const groupedContacts = useMemo(() => {
    const groups: Record<string, UniversalContact[]> = {};

    contacts.forEach(contact => {
      let groupKey = 'Autres';
      
      if (contact.source === 'family_member') {
        groupKey = 'Famille';
      } else if (contact.source === 'person_profile') {
        switch (contact.category) {
          case 'family':
            groupKey = 'Famille';
            break;
          case 'friends':
            groupKey = 'Amis';
            break;
          case 'medical_other':
            groupKey = 'Médical';
            break;
          case 'extended_family':
            groupKey = 'Famille élargie';
            break;
          default:
            groupKey = 'Contacts';
        }
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(contact);
    });

    // Sort groups: Family first, then others alphabetically
    const sortedGroups: Record<string, UniversalContact[]> = {};
    const groupOrder = ['Famille', 'Famille élargie', 'Amis', 'Médical', 'Contacts', 'Autres'];
    
    groupOrder.forEach(groupName => {
      if (groups[groupName] && groups[groupName].length > 0) {
        sortedGroups[groupName] = groups[groupName].sort((a, b) => 
          a.displayName.localeCompare(b.displayName)
        );
      }
    });

    return sortedGroups;
  }, [contacts]);

  // Get the actual ID to use for database operations
  const getActualId = (contact: UniversalContact) => {
    if (contact.source === 'family_member') {
      return contact.originalData.id; // family_member.id
    } else {
      return contact.originalData.id; // person_profile.id
    }
  };

  // Get profile_id for task list member operations (only for family members with profile_id)
  const getProfileId = (contact: UniversalContact): string | null => {
    if (contact.source === 'family_member' && contact.profile_id) {
      return contact.profile_id;
    }
    if (contact.source === 'person_profile' && contact.originalData.profile_id) {
      return contact.originalData.profile_id;
    }
    return null;
  };

  // For task assignment: get the raw UUID (profile_id or person_profile.id)
  const getTaskAssignmentId = (contact: UniversalContact): string | null => {
    // For family members with profile_id, use profile_id
    if (contact.source === 'family_member' && contact.profile_id) {
      return contact.profile_id;
    }
    // For person profiles with profile_id, use profile_id
    if (contact.source === 'person_profile' && contact.originalData.profile_id) {
      return contact.originalData.profile_id;
    }
    // For person profiles without profile_id, use their own ID
    if (contact.source === 'person_profile') {
      return contact.originalData.id;
    }
    return null;
  };

  // For events: get profile_id only (events only support real users)
  const getEventAttendeeId = (contact: UniversalContact): string | null => {
    if (contact.source === 'family_member' && contact.profile_id) {
      return contact.profile_id;
    }
    if (contact.source === 'person_profile' && contact.originalData.profile_id) {
      return contact.originalData.profile_id;
    }
    return null;
  };

  // For documents: use profile_id for family members, person_profile.id for others
  const getDocumentAssignmentId = (contact: UniversalContact): string => {
    if (contact.source === 'family_member' && contact.profile_id) {
      return contact.profile_id;
    }
    return getActualId(contact);
  };

  // Contacts that can be added to task lists (now includes all contacts)
  const getEligibleForTaskLists = () => {
    return contacts; // include all; we'll distinguish types via prefixes
  };

  return {
    contacts,
    groupedContacts,
    loading,
    getActualId,
    getProfileId,
    getTaskAssignmentId,
    getEventAttendeeId,
    getDocumentAssignmentId,
    getEligibleForTaskLists
  };
}